import { useCallback, useEffect, useState } from "react";
import { SafeAreaView, ScrollView, View } from "react-native";
import { useFocusEffect, useNavigation } from "@react-navigation/native";
import { AsyncStorageKeys, CarActivity, Pages } from "../../Types";
import MainLayoutWithSideNav from "../../components/Layout/MainLayoutWithSideNav";
import TabsView from "../../components/TabsView";
import SettingAccount from "./SettingsAccount";
import SettingsHelpAndAbout from "./SettingsHelpAndAbout";
import SettingsLegal from "./SettingsLegal";
import SettingsAdvance from "./SettingsAdvance";
import SettingsAddScreen from "./SettingsAddScreen";
import TouchableButton from "../../components/TouchableButton";
import CarActivityDialog from "../../components/CarActivityDialog";
import useGenericContentStore from "../../store/genericContent.store";
import useDrivingStore from "../../store/useDriving.store";
import useToggleStore from "../../store/useToggle.store";
import ComponentTypeEnum from "../../models/ComponentTypeEnum";
import ComponentThemeType from "../../models/ComponentTheme";
import { light_black, spacing_l } from "../../StyleHelpers";
import { StyledButtonContainer, StyledButtonSettings } from "./styled";
import { DEFAULT_ENV } from "../../config/ENV_CONFIG";
import ENVIRONMENT from "../../config/ENVIRONMENT";
import { globalAny, recordAnalytics, refreshPage } from "../../utils/Utils";
import { routeHome, routeSearchScreen } from "../../Routes";
import { RetryCommand } from "../../enums/RetryCommand";
import useRetryCommand from "../../hooks/userRetryCommand";
import { EventType } from "../../enums/EventType";

const SettingsScreen = () => {
	const navigation = useNavigation();
	const { carActivity } = useDrivingStore((state) => ({ carActivity: state.carActivity }));
	const { isToggleAdvanceSettings } = useToggleStore((state: any) => ({ isToggleAdvanceSettings: state.isToggleAdvanceSettings }));
	const setRetryCommand = useGenericContentStore((state: any) => state.setRetryCommand);
	const { isHide, setIsHide, selectedTabs, isEnabledFloatingButton, isNarrow } = useGenericContentStore((state) => ({
		isHide: state.isHide,
		setIsHide: state.setIsHide,
		selectedTabs: state.selectedTabs,
		isEnabledFloatingButton: state.isEnabledFloatingButton,
		isNarrow: state.isNarrow,
	}));

	// Local state
	const [modalLogout, setModalLogout] = useState(false);
	const [isMHU, setIsMHU] = useState(false);

	// Styles
	const styleSafeAreaView = {
		flex: 1,
		backgroundColor: light_black,
		paddingTop: isNarrow ? 0 : 112,
	};

	// Menu items
	const baseMenu = [
		{ title: globalAny.language.account, disabled: false },
		{ title: globalAny.language.help_and_about, disabled: false },
		{ title: globalAny.language.legal, disabled: false },
		{ title: globalAny.language.add_screen, disabled: false },
		{ title: globalAny.language.advanced, disabled: false },
	];

	/**
	 * Filters the base menu items based on certain conditions:
	 * - Hides the "Advanced" menu item if `isHide` is true or `isToggleAdvanceSettings` is false.
	 * - Hides the "Add Screen" menu item if `isMHU` is false.
	 *
	 * @returns {Array} - The filtered array of menu items.
	 */
	const filterMenu = () => {
		return baseMenu.filter((item) => {
			if ((isHide || !isToggleAdvanceSettings) && item.title === globalAny.language.advanced) return false;
			if (!isMHU && item.title === globalAny.language.add_screen) return false;
			return true;
		});
	};

	/**
	 * Handles the logout trigger by briefly showing a logout confirmation modal.
	 * The modal is set to visible, then automatically hidden after 1 second.
	 */
	const handleLogout = () => {
		setModalLogout(true);
		setTimeout(() => setModalLogout(false), 1000);
	};

	/**
	 * Navigates to the search screen using the provided route name.
	 * TypeScript check is ignored due to potential dynamic route type.
	 */
	const handleSearch = () => {
		//@ts-ignore
		navigation.navigate(routeSearchScreen);
	};

	/**
	 * Renders the main content inside a tab view for settings.
	 *
	 * - Tabs are filtered by `filterMenu()` to control which tabs show.
	 * - Includes `SettingAccount` component as the first tab content.
	 * - Renders a styled logout button with the `handleLogout` handler.
	 * - Renders other settings-related components in separate tabs:
	 *   `SettingsHelpAndAbout`, `SettingsLegal`, and either `SettingsAddScreen` or `SettingsAdvance` depending on `isMHU`.
	 * - Always includes an additional `SettingsAdvance` tab at the end.
	 *
	 * @returns {JSX.Element} - The rendered tab view with all settings tabs and content.
	 */
	const renderTabsContent = () => (
		<TabsView tabsToRender={filterMenu()} isSettings>
			<>
				<SettingAccount />
				{/* @ts-ignore */}
				<StyledButtonContainer>
					{/* @ts-ignore */}
					<StyledButtonSettings>
						<TouchableButton componentTheme={ComponentThemeType.VinFast} type={ComponentTypeEnum.Secondary} onPress={handleLogout}>
							{globalAny.language.logout}
						</TouchableButton>
					</StyledButtonSettings>
				</StyledButtonContainer>
			</>
			<SettingsHelpAndAbout />
			<SettingsLegal />
			{isMHU ? <SettingsAddScreen /> : <SettingsAdvance />}
			<SettingsAdvance />
		</TabsView>
	);

	/**
	 * Runs once on component mount to initialize state variables:
	 * - `isHide` is set to true if the app is running in the production environment.
	 * - `isMHU` is set based on the value stored in localStorage under the key `isInCarScreen`.
	 */
	useEffect(() => {
		setRetryCommand(RetryCommand.Settings);
		setIsHide(DEFAULT_ENV === ENVIRONMENT.PRODUCTION);
		setIsMHU(localStorage.getItem(AsyncStorageKeys.isInCarScreen) === "true");
	}, []);

	useFocusEffect(
		useCallback(() => {
			recordAnalytics(EventType.ScreenView, { screen_name: Pages.settings });
		}, []),
	);

	useRetryCommand(RetryCommand.Settings, refreshPage);
	return (
		<SafeAreaView style={styleSafeAreaView}>
			<MainLayoutWithSideNav
				screenTitle={globalAny.language.header_settings}
				navTitle={globalAny.language.header_settings}
				showLogout={modalLogout}
				searchIcon={false}
				onPressRightIcon={handleSearch}
				isSettings
				isNarrow={isNarrow}
			>
				{isNarrow ? (
					renderTabsContent()
				) : (
					<ScrollView>
						<View style={{ marginHorizontal: spacing_l }}>{renderTabsContent()}</View>
					</ScrollView>
				)}
			</MainLayoutWithSideNav>

			<CarActivityDialog
				title={globalAny.language.car_in_motion_title}
				body={globalAny.language.car_in_motion_body}
				buttonText={globalAny.language.car_in_motion_back_to_home}
				subText={globalAny.language.car_in_motion_subtext}
				onPress={() => {
					//@ts-ignore
					navigation.navigate(routeHome);
				}}
				show={carActivity === CarActivity.INMOTION && isEnabledFloatingButton && selectedTabs !== 3}
			/>
		</SafeAreaView>
	);
};

export default SettingsScreen;
